import { useRef, type ReactNode } from 'react';
import { HotelDetailsPanel } from '@voll-intelligence/intelligence-ui-kit';
import { SidePanel, SidePanelContent } from '@voll-intelligence/ui-kit/ui/side-panel';

import type { GenUiDispatch } from '../../../shared/ui/action.js';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { genUiT } from '../../../shared/ui/i18n.js';
import type {
  ChatUiHotelOffer,
  HotelPlannerPlace,
  HotelRoomsRateFilters,
} from '@voll-intelligence/genui-contract';
import { type HotelStayEcho } from './hotel_selection.js';
import type { HotelReadCache } from './hotel_read_cache.js';
import {
  hotelFavoritePanelProp,
  selectHotelRate,
  useHotelDetailsView,
  type HotelDetailsFavoriteControl,
  type HotelRateSelection,
} from './use_hotel_details_view.js';
export {
  type HotelDetailsFavoriteControl,
  type HotelRateSelection,
} from './use_hotel_details_view.js';

export type HotelDetailsDrawerProps = {
  readonly offer: ChatUiHotelOffer;
  /** A estadia em cena, incluindo os hóspedes — o eco do pick sai daqui. */
  readonly stay: HotelStayEcho;
  readonly anchor?: HotelPlannerPlace;
  /** O recorte de tarifa da lista de onde o hotel foi aberto (#5820): os
   *  quartos abrem com esses chips quando alguma tarifa atende a todos. */
  readonly rateFilters?: HotelRoomsRateFilters;
  readonly readOnly?: boolean;
  readonly busy?: boolean;
  readonly cache: HotelReadCache;
  /** Ausente (caminho conversacional) ⇒ o cabeçalho fica só com o fechar. */
  readonly favorite?: HotelDetailsFavoriteControl;
  readonly onClose: () => void;
  /** Sink conversacional. Opcional: um host determinístico passa `onRateSelect`. */
  readonly dispatch?: GenUiDispatch;
  /** Sink determinístico: recebe a tarifa escolhida e NADA é despachado. */
  readonly onRateSelect?: (selection: HotelRateSelection) => void;
};

/**
 * A gaveta de detalhes do hotel na conversa: o `SidePanel` do ui-kit, que
 * flutua pela direita sobre a conversa. Foco, trap, Esc e a trava de rolagem
 * são do Base UI; a devolução do foco fica com o card (`finalFocus={false}`),
 * que sabe cair no marcador do hotel quando quem abriu a gaveta sumiu. Aberta
 * por cima da lista ou do mapa cheio, monta DENTRO deles (`detailsLayer` do
 * organismo): diálogo aninhado, nunca irmão.
 */
export function HotelDetailsDrawer({
  offer,
  stay,
  anchor,
  rateFilters,
  readOnly = false,
  busy = false,
  cache,
  favorite,
  onClose,
  dispatch,
  onRateSelect,
}: HotelDetailsDrawerProps): ReactNode {
  const config = useGenUiConfig();
  const { locale } = config;
  const { view, rankedRooms, retry } = useHotelDetailsView(offer, stay, cache, config, {
    anchor,
    rateFilters,
  });
  const shellRef = useRef<HTMLDivElement>(null);

  const selectRate = (roomId: string, rateId: string) => {
    selectHotelRate({ rankedRooms, roomId, rateId, locale, stay, onClose, onRateSelect, dispatch });
  };

  return (
    <SidePanel
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SidePanelContent
        ref={shellRef}
        // O foco entra na própria gaveta: o leitor de tela anuncia o hotel
        // antes do primeiro controle.
        initialFocus={shellRef}
        finalFocus={false}
        showCloseButton={false}
        // O degrau da gaveta na pilha lógica de `hasForegroundDialog`, que a
        // faixa de busca da lista lê para não recolher por baixo dela.
        data-hotel-details-drawer="true"
        aria-label={genUiT(locale, 'hotelDetails.aria')}
        aria-busy={busy || undefined}
        className="gap-0 bg-card"
      >
        <HotelDetailsPanel
          {...view}
          readOnly={readOnly}
          busy={busy}
          {...hotelFavoritePanelProp(favorite, locale)}
          onClose={onClose}
          onRetry={retry}
          onRateSelect={selectRate}
          onRoomDetailsOpen={() => {}}
        />
      </SidePanelContent>
    </SidePanel>
  );
}
